import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Unplug } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, googleConnectStart, googleDisconnect } from "../../lib/api";

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

export function ConnectButton({ label = "Connect Google Analytics" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { url } = await googleConnectStart();
      window.location.assign(url);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid="google-connect-button"
      className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-surface border border-neutral-300 text-sm font-semibold text-neutral-900 hover:border-indigo-800 shadow-sm disabled:opacity-60 transition-colors">
      {busy ? <Loader2 size={16} className="animate-spin" /> : <GoogleMark />} {label}
    </button>
  );
}

export function ConnectPanel({ status, isOwner }) {
  const reconnect = status?.needs_reconnect;
  return (
    <div className="bg-surface border border-amber-200 rounded-xl p-7 max-w-2xl" data-testid="analytics-not-configured">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
        <div className="min-w-0">
          <h2 className="font-playfair text-xl text-neutral-900 mb-2">
            {reconnect ? "Google access needs to be renewed" : "Google Analytics isn't connected"}
          </h2>
          {!isOwner && <p className="text-sm text-neutral-600">Ask the site owner to connect Google Analytics.</p>}
          {isOwner && !status?.oauth_ready && (
            <p className="text-sm text-neutral-600 leading-relaxed" data-testid="oauth-not-ready">
              The Google sign-in client hasn't been added to the backend yet. Once it's saved, a "Connect Google Analytics" button appears here.
            </p>
          )}
          {isOwner && status?.oauth_ready && (
            <>
              <p className="text-sm text-neutral-600 leading-relaxed mb-5">
                Sign in with a Google account that can view GA4 property {status.property_id}. You'll approve read-only access once; no key file is needed.
              </p>
              <ConnectButton label={reconnect ? "Reconnect Google Analytics" : "Connect Google Analytics"} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ConnectionBar({ status, isOwner, onDisconnected }) {
  const [busy, setBusy] = useState(false);
  if (status?.method !== "google_account") return null;
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Analytics? The dashboard will stop showing data until you reconnect.")) return;
    setBusy(true);
    try {
      await googleDisconnect();
      toast.success("Google Analytics disconnected");
      onDisconnected();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-surface border border-neutral-200 rounded-xl px-5 py-3 text-sm" data-testid="analytics-connection">
      <span className="flex items-center gap-2 text-neutral-700 min-w-0">
        <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
        <span className="truncate">Connected as <strong className="text-neutral-900" data-testid="analytics-connected-email">{status.connected_email || "Google account"}</strong></span>
      </span>
      {isOwner && (
        <button type="button" onClick={disconnect} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 hover:text-red-700 transition-colors" data-testid="google-disconnect-button">
          <Unplug size={13} /> Disconnect
        </button>
      )}
    </div>
  );
}

export const CONNECT_MESSAGES = {
  connected: ["success", "Google Analytics connected."],
  denied: ["error", "Google access wasn't granted."],
  scope_missing: ["error", "Please tick the Google Analytics permission when approving access."],
  no_refresh_token: ["error", "Google didn't return long-term access. Remove SaaSRadarHub from your Google account's third-party access, then connect again."],
  exchange_failed: ["error", "Google sign-in couldn't be completed. Check the OAuth client settings and try again."],
  invalid_state: ["error", "That sign-in link expired. Please try connecting again."],
  not_signed_in: ["error", "Your admin session expired. Sign in and connect again."],
};
